// ---------------------------------------------------------------------------
// EntityManager.js — Corporate Entity Structure (Warm Stone Design)
// ---------------------------------------------------------------------------
import React, { useEffect, useState, useCallback } from "react";
import api from "../api/client";
import theme from "../components/theme";
import {
  Card,
  PageHeader,
  Badge,
  Button,
  Modal,
  Input,
  Select,
  TextArea,
  EmptyState,
  LoadingSpinner,
  Divider,
} from "../components/ui";

const t = theme;

const ENTITY_TYPES = [
  { value: "", label: "— Select Type —" },
  { value: "ManagementCo", label: "🏢 Management Company" },
  { value: "GP", label: "🔑 General Partner" },
  { value: "LP", label: "👤 Limited Partner" },
  { value: "SPV", label: "📁 Special Purpose Vehicle" },
  { value: "Fund", label: "💰 Fund" },
  { value: "HoldingCo", label: "🏛 Holding Company" },
  { value: "Feeder", label: "🔗 Feeder" },
  { value: "Blocker", label: "🛡 Blocker" },
  { value: "Other", label: "📋 Other" },
];

const TYPE_COLORS = {
  ManagementCo: "amber",
  GP: "blue",
  LP: "green",
  SPV: "gray",
  Fund: "amber",
  HoldingCo: "blue",
  Feeder: "gray",
  Blocker: "gray",
  Other: "gray",
};

// ── Tree Node Component ──────────────────────────────────────────────────
function TreeNode({ entity, level, selected, onSelect, entities }) {
  const [expanded, setExpanded] = useState(true);
  const children = entities.filter((e) => e.parent === entity.id);
  const isSelected = selected?.id === entity.id;

  return (
    <div>
      <div
        onClick={() => onSelect(entity)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 12px",
          paddingLeft: 12 + level * 20,
          cursor: "pointer",
          background: isSelected ? t.accentLight : "transparent",
          borderLeft: isSelected ? `3px solid ${t.accent}` : "3px solid transparent",
          borderRadius: isSelected ? `0 ${t.radiusSm} ${t.radiusSm} 0` : 0,
          transition: t.transitionFast,
          fontSize: 13,
          color: isSelected ? t.accent : t.text,
          fontWeight: isSelected ? 600 : 400,
        }}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.background = t.bgSubtle;
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.background = "transparent";
        }}
      >
        {children.length > 0 && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            style={{
              fontSize: 10,
              color: t.textMuted,
              width: 14,
              textAlign: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {expanded ? "▼" : "▶"}
          </span>
        )}
        {children.length === 0 && <span style={{ width: 14, flexShrink: 0 }} />}

        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {entity.name}
          {entity.is_default && (
            <span style={{ marginLeft: 4, color: t.sidebarActive, fontSize: 11 }}>★</span>
          )}
        </span>

        <Badge
          text={entity.entity_type || "—"}
          variant={TYPE_COLORS[entity.entity_type] || "gray"}
        />

        {!entity.is_active && (
          <Badge text="Inactive" variant="gray" />
        )}
      </div>

      {expanded &&
        children.map((child) => (
          <TreeNode
            key={child.id}
            entity={child}
            level={level + 1}
            selected={selected}
            onSelect={onSelect}
            entities={entities}
          />
        ))}
    </div>
  );
}

// ── Detail Form ──────────────────────────────────────────────────────────
function EntityForm({ entity, entities, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name: "",
    short_name: "",
    entity_type: "",
    parent: "",
    legal_name: "",
    ein: "",
    jurisdiction: "",
    formation_date: "",
    is_investing_entity: false,
    is_default: false,
    is_active: true,
    sort_order: 0,
    notes: "",
  });

  useEffect(() => {
    if (entity) {
      setForm({
        name: entity.name || "",
        short_name: entity.short_name || "",
        entity_type: entity.entity_type || "",
        parent: entity.parent || "",
        legal_name: entity.legal_name || "",
        ein: entity.ein || "",
        jurisdiction: entity.jurisdiction || "",
        formation_date: entity.formation_date || "",
        is_investing_entity: entity.is_investing_entity || false,
        is_default: entity.is_default || false,
        is_active: entity.is_active !== false,
        sort_order: entity.sort_order || 0,
        notes: entity.notes || "",
      });
    }
  }, [entity]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const parentOptions = [
    { value: "", label: "(None — Top Level)" },
    ...entities
      .filter((e) => !entity || e.id !== entity.id)
      .map((e) => ({ value: e.id, label: `${e.name} (${e.entity_type})` })),
  ];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="Display name"
        />
        <Input
          label="Short Name"
          value={form.short_name}
          onChange={(e) => set("short_name", e.target.value)}
          placeholder="Abbreviation"
        />
        <Select
          label="Entity Type"
          value={form.entity_type}
          onChange={(e) => set("entity_type", e.target.value)}
          options={ENTITY_TYPES}
        />
        <Select
          label="Parent Entity"
          value={form.parent}
          onChange={(e) => set("parent", e.target.value)}
          options={parentOptions}
        />
      </div>

      <Divider spacing={12} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <Input
          label="Legal Name"
          value={form.legal_name}
          onChange={(e) => set("legal_name", e.target.value)}
          placeholder="Full legal name"
        />
        <Input
          label="EIN / Tax ID"
          value={form.ein}
          onChange={(e) => set("ein", e.target.value)}
          placeholder="XX-XXXXXXX"
        />
        <Input
          label="Jurisdiction"
          value={form.jurisdiction}
          onChange={(e) => set("jurisdiction", e.target.value)}
          placeholder="e.g. Delaware, Cayman Islands"
        />
        <Input
          label="Formation Date"
          type="date"
          value={form.formation_date}
          onChange={(e) => set("formation_date", e.target.value)}
        />
      </div>

      <Divider spacing={12} />

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: t.text, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={form.is_investing_entity}
            onChange={(e) => set("is_investing_entity", e.target.checked)}
            style={{ accentColor: t.accent }}
          />
          Investing Entity
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: t.text, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => set("is_default", e.target.checked)}
            style={{ accentColor: t.accent }}
          />
          Default Entity
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: t.text, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
            style={{ accentColor: t.accent }}
          />
          Active
        </label>
        <Input
          label="Sort Order"
          type="number"
          value={form.sort_order}
          onChange={(e) => set("sort_order", parseInt(e.target.value) || 0)}
          style={{ width: 80 }}
        />
      </div>

      <TextArea
        label="Notes"
        value={form.notes}
        onChange={(e) => set("notes", e.target.value)}
        placeholder="Internal notes…"
      />

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button onClick={() => onSave(form)} disabled={saving || !form.name || !form.entity_type}>
          {saving ? "Saving…" : entity?.id ? "Update Entity" : "Create Entity"}
        </Button>
      </div>
    </div>
  );
}

// A server error used to arrive here as Django's HTML 500 page, and
// JSON.stringify put the whole document in the banner. The API now answers
// with {detail, error, reference} (see config/exception_handler.py), so show
// the sentence, the database's own message and the log reference — that last
// one is what ties a screenshot to a line in the Railway log.
function describeApiError(e, fallback) {
  const data = e?.response?.data;
  if (!data) return e?.message || fallback;
  if (typeof data === "string") return data.slice(0, 300) || fallback;
  const parts = [];
  if (data.detail) parts.push(data.detail);
  if (data.error) parts.push(data.error);
  if (data.reference) parts.push(`(reference ${data.reference})`);
  if (parts.length) return parts.join(" — ");
  // DRF field validation: {"name": ["This field is required."]}
  const fields = Object.entries(data)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : v}`)
    .join("; ");
  return fields || fallback;
}

// ═════════════════════════════════════════════════════════════════════════
// MAIN — Entity Manager
// ═════════════════════════════════════════════════════════════════════════
export default function EntityManager() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/core/entities/");
      setEntities(res.data.results || res.data || []);
    } catch (e) {
      console.error("Failed to load entities:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roots = entities.filter((e) => !e.parent);

  const handleSave = async (formData) => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        parent: formData.parent || null,
        sort_order: formData.sort_order || 0,
      };

      if (selected?.id && !showAdd) {
        await api.put(`/core/entities/${selected.id}/`, payload);
      } else {
        await api.post("/core/entities/", payload);
        setShowAdd(false);
      }
      await load();
    } catch (e) {
      setError(describeApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selected) return;
    if (!window.confirm(`Deactivate "${selected.name}"? This won't delete data.`)) return;
    try {
      await api.patch(`/core/entities/${selected.id}/`, { is_active: false });
      await load();
      setSelected(null);
    } catch (e) {
      setError(describeApiError(e, "Deactivation failed"));
    }
  };

  if (loading) return <LoadingSpinner message="Loading entities..." />;

  // Counts
  const activeCount = entities.filter((e) => e.is_active).length;
  const investingCount = entities.filter((e) => e.is_investing_entity).length;

  return (
    <div>
      <PageHeader
        title="Entity Manager"
        subtitle="Corporate structure hierarchy"
        action={
          <Button onClick={() => { setShowAdd(true); setSelected(null); }}>
            + Add Entity
          </Button>
        }
      />

      {/* Summary bar */}
      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 16,
          fontSize: 12,
          color: t.textMuted,
        }}
      >
        <span>Total: <strong style={{ color: t.text }}>{entities.length}</strong></span>
        <span>Active: <strong style={{ color: t.success }}>{activeCount}</strong></span>
        <span>Investing: <strong style={{ color: t.accent }}>{investingCount}</strong></span>
      </div>

      {error && (
        <div
          style={{
            padding: "10px 16px",
            background: t.dangerBg,
            border: `1px solid ${t.dangerBorder}`,
            borderRadius: t.radius,
            color: t.danger,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              float: "right",
              background: "none",
              border: "none",
              color: t.danger,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, alignItems: "start" }}>
        {/* ── LEFT: Tree ──────────────────────────────────────────── */}
        <Card title="Hierarchy" noPad style={{ position: "sticky", top: 16 }}>
          <div style={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto", padding: "8px 0" }}>
            {roots.length === 0 ? (
              <EmptyState message="No entities — add one to get started" />
            ) : (
              roots.map((root) => (
                <TreeNode
                  key={root.id}
                  entity={root}
                  level={0}
                  selected={selected}
                  onSelect={(e) => {
                    setSelected(e);
                    setShowAdd(false);
                  }}
                  entities={entities}
                />
              ))
            )}
          </div>
        </Card>

        {/* ── RIGHT: Detail Form ──────────────────────────────────── */}
        <Card title={showAdd ? "New Entity" : selected ? `Edit: ${selected.name}` : "Entity Details"}>
          {!selected && !showAdd ? (
            <EmptyState
              message="Select an entity from the tree, or click + Add Entity"
              icon="◨"
            />
          ) : (
            <EntityForm
              entity={showAdd ? null : selected}
              entities={entities}
              onSave={handleSave}
              onCancel={showAdd ? () => setShowAdd(false) : null}
              saving={saving}
            />
          )}

          {selected && !showAdd && (
            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: `1px solid ${t.borderLight}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 11, color: t.textMuted }}>
                ID: {selected.id}
                {selected.created_at && ` · Created: ${selected.created_at.slice(0, 10)}`}
                {selected.updated_at && ` · Updated: ${selected.updated_at.slice(0, 10)}`}
              </div>
              <Button variant="danger" size="sm" onClick={handleDeactivate}>
                Deactivate
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
